"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import {
	createContext,
	type ReactNode,
	useContext,
	useEffect,
	useId,
	useRef,
	useState,
} from "react";
import { NowPlaying } from "@/components/editorial-system/audio/now-playing";
import { useComposer } from "@/components/editorial-system/composer/composer-context";
import {
	type EditorialNavLink,
	type EditorialPlaceNav,
	isNavLinkActive,
} from "@/components/editorial-system/editorial-nav-shared";

/*
 * Phone bottom bar: where you are (opens the key views) and Post (opens the
 * composer, `EditorialComposer`). Menu, with
 * everything, stays in the top band. Each page tells
 * the bar its place, live line and own action through `EditorialPhonePage`.
 * Registration runs in an effect, so a page Next.js keeps mounted but hidden
 * (React Activity) drops out of the bar until it is shown again.
 */

export interface EditorialPhonePageInfo {
	place?: EditorialPlaceNav;
	pulse?: { label: string; live?: boolean };
	action?: { label: string; href?: string; node?: ReactNode };
}

type Registered = EditorialPhonePageInfo & { id: string };

const PhonePageContext = createContext<{
	page: Registered | null;
	setPage: (update: (prev: Registered | null) => Registered | null) => void;
} | null>(null);

export function EditorialPhoneProvider({ children }: { children: ReactNode }) {
	const [page, setPage] = useState<Registered | null>(null);
	return (
		<PhonePageContext.Provider value={{ page, setPage }}>
			{children}
		</PhonePageContext.Provider>
	);
}

/** Rendered by `EditorialChrome`: hands this page's pieces to the bar. */
export function EditorialPhonePage({
	place,
	pulse,
	action,
}: EditorialPhonePageInfo) {
	const context = useContext(PhonePageContext);
	const id = useId();
	const setPage = context?.setPage;
	const info = useRef<EditorialPhonePageInfo>({ place, pulse, action });
	info.current = { place, pulse, action };

	// biome-ignore lint/correctness/useExhaustiveDependencies: re-register when what the bar shows changes; the latest values are read from the ref
	useEffect(() => {
		if (!setPage) return;
		setPage(() => ({ id, ...info.current }));
		return () => setPage((prev) => (prev?.id === id ? null : prev));
	}, [
		setPage,
		id,
		place?.href,
		place?.title,
		pulse?.label,
		pulse?.live,
		action?.label,
		action?.href,
	]);

	return null;
}

/** The page in view, as registered by its `EditorialChrome`. */
export function useEditorialPhonePage(): EditorialPhonePageInfo | null {
	return useContext(PhonePageContext)?.page ?? null;
}

/** The views the bar jumps between; the rest live in Menu. */
const KEY_VIEWS = ["/", "/discussions", "/talk", "/scenes", "/members"];

const OTHER_LABELS: [string, string][] = [
	["/notifications", "Notifications"],
	["/messages", "Messages"],
	["/profile", "Profile"],
	["/saved", "Saved"],
	["/your-music", "Your music"],
	["/admin", "Admin"],
];

function placeLabel(
	pathname: string,
	primary: EditorialNavLink[],
	page: Registered | null,
) {
	if (page?.place && !page.place.flat) return page.place.title;
	const link = primary.find((item) => isNavLinkActive(item, pathname));
	if (link) return link.label;
	return (
		OTHER_LABELS.find(([href]) => pathname.startsWith(href))?.[1] ?? "Views"
	);
}

export function EditorialPhoneBar({
	primary,
	signedIn,
}: {
	primary: EditorialNavLink[];
	signedIn: boolean;
}) {
	const context = useContext(PhonePageContext);
	const composer = useComposer();
	const page = context?.page ?? null;
	const pathname = usePathname() ?? "/";
	const [viewsOpen, setViewsOpen] = useState(false);
	const viewsId = useId();
	const viewsButtonRef = useRef<HTMLButtonElement>(null);
	const panelRef = useRef<HTMLElement>(null);

	// biome-ignore lint/correctness/useExhaustiveDependencies: close on navigation
	useEffect(() => {
		setViewsOpen(false);
	}, [pathname]);

	useEffect(() => {
		if (!viewsOpen) return;
		panelRef.current
			?.querySelector<HTMLElement>("a, button")
			?.focus({ preventScroll: true });
		const onKey = (event: KeyboardEvent) => {
			if (event.key === "Escape") {
				setViewsOpen(false);
				viewsButtonRef.current?.focus();
			}
		};
		window.addEventListener("keydown", onKey);
		return () => window.removeEventListener("keydown", onKey);
	}, [viewsOpen]);

	const label = placeLabel(pathname, primary, page);
	const live = page?.pulse?.live ? page.pulse.label : null;
	const views = KEY_VIEWS.map((href) =>
		primary.find((item) => item.href === href),
	).filter((item): item is EditorialNavLink => Boolean(item));
	// The page's own action (Reply, Message, Leave…) leads the list, since
	// Post now opens the composer.
	const action =
		page?.action && page.action.href !== "/create-post" ? page.action : null;

	return (
		<div className="ed-phonebar" data-open={viewsOpen ? "true" : undefined}>
			{viewsOpen ? (
				<>
					<button
						type="button"
						aria-label="Close"
						tabIndex={-1}
						className="ed-post-veil"
						onClick={() => setViewsOpen(false)}
					/>
					<nav
						ref={panelRef}
						id={viewsId}
						aria-label="Key views"
						className="ed-post-panel"
					>
						{action ? (
							action.node ? (
								<div className="ed-post-panel__row ed-views__row">
									{action.node}
									<span className="ed-post-panel__hint">On this page</span>
								</div>
							) : action.href ? (
								<Link
									href={action.href}
									className="ed-post-panel__row ed-views__row"
									onClick={() => setViewsOpen(false)}
								>
									<span className="ed-post-panel__label">{action.label}</span>
									<span className="ed-post-panel__hint">On this page</span>
								</Link>
							) : null
						) : null}
						{views.map((view) => {
							const active = isNavLinkActive(view, pathname);
							return (
								<Link
									key={view.href}
									href={view.href}
									aria-current={active ? "page" : undefined}
									className="ed-post-panel__row ed-views__row"
									onClick={() => setViewsOpen(false)}
								>
									<span
										className="ed-post-panel__label"
										data-active={active ? "true" : undefined}
									>
										{view.label}
									</span>
								</Link>
							);
						})}
					</nav>
				</>
			) : null}

			<div className="ed-phonebar__playing">
				<NowPlaying />
			</div>

			<div className="ed-phonebar__row">
				<button
					ref={viewsButtonRef}
					type="button"
					className="ed-phonebar__place"
					aria-expanded={viewsOpen}
					aria-controls={viewsId}
					aria-label={`${label}: switch view`}
					onClick={() => setViewsOpen((value) => !value)}
				>
					<span className="ed-phonebar__label">
						{label}{" "}
						<span aria-hidden className="ed-secondary">
							{viewsOpen ? "−" : "+"}
						</span>
					</span>
					{live ? (
						<span className="ed-meta ed-secondary ed-row" style={{ gap: 6 }}>
							<span aria-hidden className="ed-dot" />
							{live}
						</span>
					) : null}
				</button>
				{signedIn ? (
					<button
						type="button"
						className="ed-post-button"
						aria-haspopup="dialog"
						aria-expanded={composer?.open ?? false}
						onClick={() => {
							setViewsOpen(false);
							composer?.openComposer();
						}}
					>
						Post
					</button>
				) : (
					<Link href="/apply" className="ed-post-button">
						Join
					</Link>
				)}
			</div>
		</div>
	);
}
